import { Controller } from "@hotwired/stimulus"
import IMask from "imask"

export default class extends Controller {
  static targets = [
    "startsAt", "endsAt",
    "startsAtDate", "startsAtTime", "startsAtDropdown",
    "endsAtDate", "endsAtTime", "endsAtDropdown",
    "allDay", "timePart"
  ]

  static values = {
    startsAt: String,
    endsAt: String,
  }

  connect() {
    const timeMaskOptions = {
      mask: "HH:MM",
      blocks: {
        HH: { mask: IMask.MaskedRange, from: 0, to: 23 },
        MM: { mask: IMask.MaskedRange, from: 0, to: 59 }
      }
    }
    this.startTimeMask = IMask(this.startsAtTimeTarget, timeMaskOptions)
    this.endTimeMask = IMask(this.endsAtTimeTarget, timeMaskOptions)

    const starts = this.startsAtValue || ""
    const ends = this.endsAtValue || ""

    if (starts) {
      const [date, time] = starts.split("T")
      this.startsAtDateTarget.value = date
      this.startsAtTimeTarget.value = time || ""
      this.startsAtTarget.value = starts
    } else {
      // Default: today at 09:00
      const today = new Date().toISOString().slice(0, 10)
      this.startsAtDateTarget.value = today
      this.startsAtTimeTarget.value = "09:00"
      this.startsAtTarget.value = `${today}T09:00`
    }

    if (ends) {
      const [date, time] = ends.split("T")
      this.endsAtDateTarget.value = date
      this.endsAtTimeTarget.value = time || ""
      this.endsAtTarget.value = ends
    } else {
      // Default: start + 1h
      const startDate = this.startsAtDateTarget.value
      const startMins = this._timeToMinutes(this.startsAtTimeTarget.value || "09:00")
      const endMins = startMins + 60
      const endDate = endMins >= 1440
        ? this._addDays(startDate, 1)
        : startDate
      const endTime = this._minutesToTime(endMins % 1440)
      this.endsAtDateTarget.value = endDate
      this.endsAtTimeTarget.value = endTime
      this.endsAtTarget.value = `${endDate}T${endTime}`
    }

    if (this.hasAllDayTarget && this.allDayTarget.checked) {
      this.timePartTarget.classList.add("hidden")
    }
  }

  disconnect() {
    this.startTimeMask?.destroy()
    this.endTimeMask?.destroy()
  }

  allDayChanged() {
    this.timePartTarget.classList.toggle("hidden", this.allDayTarget.checked)
  }

  startsAtChanged() {
    this._syncStartsAt()
  }

  endsAtChanged() {
    this._syncEndsAt()
  }

  startsAtTimeTyped() {
    const val = this.startsAtTimeTarget.value
    if (this._isValidTime(val)) {
      this._syncStartsAt()
    }
  }

  endsAtTimeTyped() {
    const val = this.endsAtTimeTarget.value
    if (this._isValidTime(val)) {
      this._syncEndsAt()
    }
  }

  commitStartTime() {
    const val = this.startsAtTimeTarget.value.trim()
    const parsed = this._parseTimeInput(val)
    if (parsed) {
      this.startsAtTimeTarget.value = parsed
      this._syncStartsAt()
    }
    this._hideDropdown(this.startsAtDropdownTarget)
  }

  commitEndTime() {
    const val = this.endsAtTimeTarget.value.trim()
    const parsed = this._parseTimeInput(val)
    if (parsed) {
      this.endsAtTimeTarget.value = parsed
      this._syncEndsAt()
    }
    this._hideDropdown(this.endsAtDropdownTarget)
  }

  showStartDropdown() {
    const dropdown = this.startsAtDropdownTarget
    const currentTime = this.startsAtTimeTarget.value
    this._populateTimeDropdown(dropdown, currentTime, null, (time) => {
      this.startsAtTimeTarget.value = time
      this._syncStartsAt()
      this._hideDropdown(dropdown)
    })
    dropdown.classList.remove("hidden")
    this._scrollToTime(dropdown, currentTime)
  }

  hideStartDropdown() {
    setTimeout(() => this._hideDropdown(this.startsAtDropdownTarget), 150)
  }

  showEndDropdown() {
    const dropdown = this.endsAtDropdownTarget
    const currentTime = this.endsAtTimeTarget.value
    const startTime = this.startsAtTimeTarget.value
    const startDate = this.startsAtDateTarget.value
    const endDate = this.endsAtDateTarget.value
    const sameDayOrEarlier = endDate <= startDate

    this._populateTimeDropdown(dropdown, currentTime, sameDayOrEarlier ? startTime : null, (time) => {
      this.endsAtTimeTarget.value = time
      this._syncEndsAt()
      this._hideDropdown(dropdown)
    }, startTime, startDate, endDate)
    dropdown.classList.remove("hidden")
    this._scrollToTime(dropdown, currentTime)
  }

  hideEndDropdown() {
    setTimeout(() => this._hideDropdown(this.endsAtDropdownTarget), 150)
  }

  // --- private ---

  _syncStartsAt() {
    const date = this.startsAtDateTarget.value
    const time = this.startsAtTimeTarget.value
    if (date && this._isValidTime(time)) {
      this.startsAtTarget.value = `${date}T${time}`
    }
  }

  _syncEndsAt() {
    const date = this.endsAtDateTarget.value
    const time = this.endsAtTimeTarget.value
    if (date && this._isValidTime(time)) {
      this.endsAtTarget.value = `${date}T${time}`
    }
  }

  _populateTimeDropdown(dropdown, currentTime, minTime, onSelect, startTime, startDate, endDate) {
    dropdown.innerHTML = ""
    const minMins = minTime ? this._timeToMinutes(minTime) + 15 : 0

    for (let m = 0; m < 1440; m += 15) {
      if (m < minMins) continue

      const timeStr = this._minutesToTime(m)
      const li = document.createElement("li")

      let label = timeStr
      if (startTime && startDate && endDate) {
        const startMins = this._timeToMinutes(startTime)
        let diffMins = m - startMins
        if (endDate > startDate) diffMins += 1440
        if (diffMins > 0) {
          label = `${timeStr} (${this._formatDuration(diffMins)})`
        }
      }

      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = `w-full text-left px-3 py-1 text-sm hover:bg-base-200${timeStr === currentTime ? " bg-base-200 font-semibold" : ""}`
      btn.textContent = label
      btn.dataset.time = timeStr
      btn.addEventListener("mousedown", (e) => e.preventDefault())
      btn.addEventListener("click", () => onSelect(timeStr))

      li.appendChild(btn)
      dropdown.appendChild(li)
    }
  }

  _scrollToTime(dropdown, timeStr) {
    if (!timeStr) return
    requestAnimationFrame(() => {
      const btn = dropdown.querySelector(`[data-time="${timeStr}"]`)
      if (btn) btn.scrollIntoView({ block: "nearest" })
    })
  }

  _hideDropdown(dropdown) {
    dropdown.classList.add("hidden")
  }

  _isValidTime(str) {
    return /^\d{2}:\d{2}$/.test(str)
  }

  _parseTimeInput(str) {
    if (!str) return null
    // Already HH:MM
    if (/^\d{2}:\d{2}$/.test(str)) return str
    // H:MM or HH:M
    const m = str.match(/^(\d{1,2}):(\d{1,2})$/)
    if (m) {
      const h = m[1].padStart(2, "0")
      const min = m[2].padStart(2, "0")
      if (parseInt(h) < 24 && parseInt(min) < 60) return `${h}:${min}`
    }
    // Just hour like "9" or "14"
    const h = str.match(/^(\d{1,2})$/)
    if (h && parseInt(h[1]) < 24) return `${h[1].padStart(2, "0")}:00`
    return null
  }

  _timeToMinutes(str) {
    if (!str || !str.includes(":")) return 0
    const [h, m] = str.split(":").map(Number)
    return h * 60 + m
  }

  _minutesToTime(totalMin) {
    const h = Math.floor(totalMin / 60) % 24
    const m = totalMin % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  _formatDuration(minutes) {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    if (h > 0 && m > 0) return `${h}h ${m}min`
    if (h > 0) return `${h}h`
    return `${m}min`
  }

  _addDays(dateStr, days) {
    const d = new Date(dateStr + "T00:00:00")
    d.setDate(d.getDate() + days)
    return d.toISOString().slice(0, 10)
  }
}
